import {faUser} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {TableColumnsType} from 'antd';

import {Text} from '../../../../../../../component/text/text';
import {Locale} from '../../../../../../../provider/locale/localization';
import {LangKeyType} from '../../../../../../../provider/locale/translation/type';
import {SourceLinkStatusEnum} from '../../../../../../../service/source-settings/source-links/source-links-type';
import {StoreAccountStatusEnum} from '../../../../../../../service/source-settings/store-accounts/store-accounts-hook';
import {NeverError} from '../../../../../../../util/error';
import {BrandsOrCompaniesCell} from '../../brands-or-companies-cell/brands-or-companies-cell';
import {StatusCell} from '../../status-table-cell/status-cell';

import {statusFilterKey} from './accounts-table-const';
import {SourceAccountsTableType, StoreAccountsTableType} from './accounts-table-type';
import * as styles from './accounts-table.scss';

function getSourceAccountStatus(status: SourceLinkStatusEnum): SourceLinkStatusEnum {
    return status;
}

function getAppStoreAccountStatus(status: StoreAccountStatusEnum): SourceLinkStatusEnum {
    switch (status) {
        case StoreAccountStatusEnum.NoError:
            return SourceLinkStatusEnum.Synced;
        case StoreAccountStatusEnum.NeedsBinding:
        case StoreAccountStatusEnum.PermissionError:
        case StoreAccountStatusEnum.InvalidCredentials:
        case StoreAccountStatusEnum.UnknownError:
            return SourceLinkStatusEnum.Fail;
        default:
            throw new NeverError(status);
    }
}

function getStoreAccountFailStatusLabelKey(status: StoreAccountStatusEnum): LangKeyType | null {
    switch (status) {
        case StoreAccountStatusEnum.NoError:
            return null;
        case StoreAccountStatusEnum.NeedsBinding:
            return 'SOURCE_SETTINGS__ACCOUNTS__STORE_STATUS__NEEDS_BINDING';
        case StoreAccountStatusEnum.PermissionError:
            return 'SOURCE_SETTINGS__ACCOUNTS__ERROR_HAS_NOT_ACCESS';
        case StoreAccountStatusEnum.InvalidCredentials:
            return 'SOURCE_SETTINGS__ACCOUNTS__ERROR_INVALID_CREDITNAILS';
        case StoreAccountStatusEnum.UnknownError:
            return 'SOURCE_SETTINGS__ACCOUNTS__STORE_STATUS__UNKNOWN_ERROR';
        default:
            throw new NeverError(status);
    }
}

export function getAccountsTableColumns(
    getFormattedDateTime: (date: Date | number) => string,
    isKeyId: boolean
): TableColumnsType<SourceAccountsTableType> {
    const dateColumns: TableColumnsType<SourceAccountsTableType> = isKeyId
        ? []
        : [
              {
                  align: 'center',
                  title: <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__COLUMN_HEADER__DATE" />,
                  key: 'date',
                  render: (_value: unknown, {updatedAt}: SourceAccountsTableType) =>
                      getFormattedDateTime(new Date(updatedAt || '')),
              },
          ];

    return [
        {
            title: isKeyId ? (
                <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__COLUMN_HEADER__KEY_ID" />
            ) : (
                <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__COLUMN_HEADER__LOGIN" />
            ),
            dataIndex: 'account',
            key: 'account',
            render: (_value: unknown, {login}: SourceAccountsTableType) =>
                isKeyId ? (
                    <Text bolder>{login}</Text>
                ) : (
                    <>
                        <FontAwesomeIcon className={styles.AccountsTable_userIcon} icon={faUser} />
                        <Text bolder>{login}</Text>
                    </>
                ),
        },
        {
            title: <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__COLUMN_HEADER__BRANDS_COMPANIES" />,
            key: 'brand',
            render: (
                _value: unknown,
                {brandsCount, companiesCount, limitedCompaniesNames, limitedBrandsNames}: SourceAccountsTableType
            ) => (
                <BrandsOrCompaniesCell
                    brandsCount={brandsCount}
                    companiesCount={companiesCount}
                    limitedBrandsNames={limitedBrandsNames}
                    limitedCompaniesNames={limitedCompaniesNames}
                />
            ),
        },
        {
            title: <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__COLUMN_HEADER__STATUS" />,
            key: statusFilterKey,
            filters: [
                {
                    text: <Locale stringKey="SOURCE_SETTINGS__LINKS__LINKS__STATUS__SYNCED" />,
                    value: SourceLinkStatusEnum.Synced,
                },
                {
                    text: <Locale stringKey="SOURCE_SETTINGS__LINKS__LINKS__STATUS__SYNCHRONIZATION" />,
                    value: SourceLinkStatusEnum.Synchronization,
                },
                {
                    text: <Locale stringKey="SOURCE_SETTINGS__LINKS__LINKS__STATUS__FAIL" />,
                    value: SourceLinkStatusEnum.Fail,
                },
            ],
            render: (_value: unknown, {status, errorText}: SourceAccountsTableType) => (
                <StatusCell errorText={errorText} status={getSourceAccountStatus(status)} />
            ),
        },
        ...dateColumns,
        {
            align: 'center',
            title: <Locale stringKey="SOURCE_SETTINGS__LINKS__LINKS__COLUMN_HEADER__ACTIONS" />,
            dataIndex: 'actions',
        },
    ];
}

export function getStoreAccountsTableColumns(): TableColumnsType<StoreAccountsTableType> {
    return [
        {
            title: <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__COLUMN_HEADER__KEY_ID" />,
            dataIndex: 'account',
            key: 'account',
            render: (_value: unknown, {login}: StoreAccountsTableType) => <Text>{login}</Text>,
        },
        {
            title: <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__BRANDS" />,
            key: 'brand',
            render: (_value: unknown, {brandsCount, limitedBrandsNames}: StoreAccountsTableType) => (
                <BrandsOrCompaniesCell
                    brandsCount={brandsCount}
                    companiesCount={0}
                    limitedBrandsNames={limitedBrandsNames}
                    limitedCompaniesNames={[]}
                />
            ),
        },
        {
            title: <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__COLUMN_HEADER__STATUS" />,
            key: statusFilterKey,
            filters: [
                {
                    text: <Locale stringKey="SOURCE_SETTINGS__LINKS__LINKS__STATUS__SYNCED" />,
                    value: StoreAccountStatusEnum.NoError,
                },
                {
                    text: <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__STORE_STATUS__NEEDS_BINDING" />,
                    value: StoreAccountStatusEnum.NeedsBinding,
                },
                {
                    text: <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__ERROR_HAS_NOT_ACCESS" />,
                    value: StoreAccountStatusEnum.PermissionError,
                },
                {
                    text: <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__ERROR_INVALID_CREDITNAILS" />,
                    value: StoreAccountStatusEnum.InvalidCredentials,
                },
                {
                    text: <Locale stringKey="SOURCE_SETTINGS__ACCOUNTS__STORE_STATUS__UNKNOWN_ERROR" />,
                    value: StoreAccountStatusEnum.UnknownError,
                },
            ],
            render: (_value: unknown, {status, errorText}: StoreAccountsTableType) => (
                <StatusCell
                    errorText={errorText}
                    failStatusLabelStringKey={getStoreAccountFailStatusLabelKey(status)}
                    status={getAppStoreAccountStatus(status)}
                />
            ),
        },
        {
            align: 'center',
            title: <Locale stringKey="SOURCE_SETTINGS__LINKS__LINKS__COLUMN_HEADER__ACTIONS" />,
            dataIndex: 'actions',
        },
    ];
}
